// @flow

import _ from 'lodash';
import React from 'react';
import Accordion from 'client/components/accordion/accordion';
import ChevronIcon from 'client/components/icons/chevron';
import Checkmark from 'client/components/checkmark';
import EmailSubscriptionContainer from 'client/components/subscribe/email';
import HealthEntryPoint from 'client/components/ifpApp/healthEntryPoint';
import HeroContainer from 'client/components/heroContainer';
import LinkButton from 'client/components/button/linkButton';
import Panel from 'client/components/accordion/panel';
import Tabs from 'client/components/tabs/tabs';
import COLORS from 'client/constants/colors';
import {
  COVID19_HUB_CTA_CLICK,
  COVID19_HUB_EMAIL_SUBSCRIBED,
  COVID19_HUB_TAB_CLICK,
} from 'client/constants/amplitudeTracker';
import { Tracking } from 'web-core/src/tracking';
import { trackAccordionClick } from './utils/tracking';

import '../styles/covid19.scss';

// CTA component types specified in contentful
const BUTTON = 'button';
const EMAIL_SUBSCRIPTION = 'emailSubscription';
const MULTIPLE_LINKS = 'multipleLinks';
const ZIP_CODE_SEARCH = 'zipCodeSearch';

type CardType = {
  header: string,
  text: string,
  linkText: string,
  linkUrl: string,
  isPartnerCard: boolean,
};

type TriageType = {
  title: string,
  description: string,
  imgSrc: string,
  cta: {
    type:
      | typeof MULTIPLE_LINKS
      | typeof ZIP_CODE_SEARCH
      | typeof BUTTON
      | typeof EMAIL_SUBSCRIPTION,
    links: Array<{
      text: string,
      href: string,
    }>,
    text: string,
    href: string,
    list: string,
  },
};

type Props = {
  brand: string,
  contentfulData: {
    accordionDefaultOpen: boolean,
    brandConfig: {
      hero: {
        header: string,
        subheader: string,
      },
      partnerResource: CardType,
    },
    hero: {
      header: string,
      subheader: string,
    },
    firstRow: {
      title: string,
      cards: Array<CardType>,
    },
    secondRow: {
      title: string,
      cards: Array<CardType>,
    },
    triage: Array<TriageType>,
  },
};

const CovidPage = (props: Props) => {
  const {
    brand,
    contentfulData: { accordionDefaultOpen, hero, triage, firstRow, secondRow, ...brandConfig },
  } = props;

  // COVID-19 Hub page currently only enables hero and partner resources card to be customizable for partners
  const getPartnerConfig = (defaultContent: Object, dataKey: string) => {
    let partnerConfig;
    if (brandConfig[brand]) {
      partnerConfig = _.merge(defaultContent, _.get(brandConfig[brand], dataKey, {}));
    } else {
      partnerConfig = defaultContent;
    }
    return partnerConfig;
  };

  const renderCtaComponent = (cta) => {
    const ctaType = cta.type;
    switch (ctaType) {
      case ZIP_CODE_SEARCH:
        return (
          <HealthEntryPoint
            onClick={value =>
              Tracking.track(COVID19_HUB_CTA_CLICK, {
                cta: 'HealthEntryPoint',
                zipcode: value,
              })
            } />
        );

      case BUTTON:
        return (
          <LinkButton
            onClick={() => Tracking.track(COVID19_HUB_CTA_CLICK, { cta: cta.text })}
            href={cta.href}>
            <span>{cta.text}</span>
          </LinkButton>
        );

      case MULTIPLE_LINKS:
        return _.map(cta.links, link => (
          <li className="multiple-link bold" key={link.text}>
            <a
              onClick={() => Tracking.track(COVID19_HUB_CTA_CLICK, { cta: link.text })}
              href={link.href}>
              {link.text}
              <ChevronIcon className="chevron-icon" />
            </a>
          </li>
        ));

      case EMAIL_SUBSCRIPTION:
        return (
          <EmailSubscriptionContainer
            lists={[cta.list]}
            className="focus-white-color email-subscription-form"
            id={cta.list}
            submitHandler={() => Tracking.track(COVID19_HUB_EMAIL_SUBSCRIBED)}
            subscribeSuccessIcon={<Checkmark checked />}
            subscribeSuccessStyle={{
              display: 'flex',
              alignItems: 'center',
              fontSize: '16px',
              fontWeight: '600',
              marginBottom: '2rem',
              color: COLORS.NAVY_2,
            }}
            buttonClassName="primary oep-hover"
            buttonLabel={{ path: 'common.cta.sendMeUpdates' }}
            subscribedLabel={{ path: 'common.label.emailSubscribedShort' }} />
        );

      default:
        return null;
    }
  };

  const renderTabContent = () => {
    if (triage && triage.length < 1) return undefined;

    return _.map(triage, content => (
      <div label={content.title} key={content.title}>
        <div className="triage-container flex-row">
          <div className="medium-2">
            <img className="tab-icon" alt={content.title} src={content.imgSrc} />
          </div>
          <div className="medium-10">
            <h2 className="pad-bottom">{content.title}</h2>
            <div className="pad-bottom">{content.description}</div>
            {renderCtaComponent(content.cta)}
          </div>
        </div>
      </div>
    ));
  };

  const renderPanel = () => {
    if (triage.length < 1) return undefined;

    const panelContent = renderTabContent();
    return _.map(triage, (content, key: number) => (
      <Panel key={content.title} title={<span>{content.title}</span>}>
        {panelContent && panelContent[key]}
      </Panel>
    ));
  };

  const renderResourceCards = (resource) => {
    const { title, cards } = resource;
    if (cards.length < 1) return undefined;

    return (
      <section className="resource-section pad-x-2">
        {title && <h2 className="resource-section-title pad-y-2">{title}</h2>}
        <div className="resource-cards-row">
          {!_.isEmpty(cards) &&
            cards.map((card) => {
              const cardContent = card.isPartnerCard
                ? getPartnerConfig(card, 'partnerResource')
                : card;
              return (
                <a
                  key={cardContent.header}
                  alt={cardContent.header}
                  onClick={() => Tracking.track(COVID19_HUB_CTA_CLICK, { cta: cardContent.header })}
                  className="medium-4 resource-card"
                  href={cardContent.linkUrl}>
                  <h4 className="pad-top-half bold">{_.get(cardContent, 'header')}</h4>
                  <p className="pad-top-half">{_.get(cardContent, 'text')}</p>
                  <p className="pad-top-half semi-bold">
                    {cardContent.linkText} <ChevronIcon className="chevron-icon" />
                  </p>
                </a>
              );
            })}
        </div>
      </section>
    );
  };

  const trackingTabClick = (state) => {
    Tracking.track(COVID19_HUB_TAB_CLICK, state);
  };

  return (
    <div className="covid-landing">
      <HeroContainer base="covid19" {...getPartnerConfig(hero, 'hero')}>
        <Tabs defaultActiveTab className="show-for-large" onClick={trackingTabClick}>
          {renderTabContent()}
        </Tabs>
        <Accordion
          className="hide-for-large"
          initialIndex={accordionDefaultOpen ? 0 : null}
          onChange={state => trackAccordionClick(COVID19_HUB_TAB_CLICK, state)}>
          {renderPanel()}
        </Accordion>
      </HeroContainer>
      <div className="member-resource-container hero-contain">
        {firstRow && renderResourceCards(firstRow)}
        {secondRow && renderResourceCards(secondRow)}
      </div>
    </div>
  );
};

CovidPage.defaultProps = {
  contentfulData: {},
};

export default CovidPage;
